import * as React from 'react'

import { useTranslation } from 'react-i18next'

import { useAuth } from '@enechain/auth-js'
import {
  Box,
  Button,
  Flex,
  MenuContent,
  MenuPositioner,
  MenuRadioItem,
  MenuRadioItemGroup,
  MenuRoot,
  MenuTrigger,
  Separator,
  SideMenuLayoutAppSwitcherMenu,
  SideMenuLayoutPopOverMenuItem,
  SideMenuLayoutUserMenu,
} from '@enechain/ecloud-designsystem'
import {
  EnecAppswitcherRegular,
  FaChevronUpRegular,
  FaGlobeRegular,
} from '@enechain/ecloud-icons'

import { LanguageSettingHint } from '~/components/layouts/MainLayout/languageSettingHint'
import { useLanguage } from '~/providers/LanguageInitProvider'
import {
  dismissLanguageHint,
  isLanguageHintDismissed,
  isSupportedLanguage,
} from '~/providers/languageStorage'

/**
 * @memo
 * env 参照は apps 側からのみ受け取れるので props で持っているが、複数になった場合は別途対応する
 */
type Props = {
  eCloudUrl: string
}

export const MainLayoutBottomMenu: React.FC<Props> = ({ eCloudUrl }) => {
  const { t } = useTranslation(['component'])
  const { logout, user } = useAuth()
  const { locale, setLanguage } = useLanguage()
  // 言語を変更できることに気づいていない初回ユーザーに、設定箇所を案内する
  const [showLanguageHint, setShowLanguageHint] = React.useState(
    () => !isLanguageHintDismissed(),
  )

  const handleDismissLanguageHint = (): void => {
    if (showLanguageHint) {
      dismissLanguageHint()
      setShowLanguageHint(false)
    }
  }

  return (
    <>
      <SideMenuLayoutAppSwitcherMenu
        Icon={EnecAppswitcherRegular}
        items={[
          {
            name: 'eCloud',
            onClick: (): void => {
              window.location.href = eCloudUrl
            },
          },
        ]}
        label={t('component:layouts.main_layout.app_switcher')}
      />
      <Box position="relative" onClickCapture={handleDismissLanguageHint}>
        {showLanguageHint && (
          <LanguageSettingHint onDismiss={handleDismissLanguageHint} />
        )}
        <SideMenuLayoutUserMenu
          popOverMenu={
            <>
              <SideMenuLayoutPopOverMenuItem
                label={t('component:layouts.main_layout.profile')}
                onClick={(): void => {
                  window.location.href = eCloudUrl + '/profile'
                }}
              />
              <MenuRoot positioning={{ placement: 'top-start', gutter: 8 }}>
                <MenuTrigger
                  textStyle="textMd"
                  _focus={{
                    boxShadow: `0 0 0 2px {colors.Object.accentPrimary}`,
                  }}
                  _hover={{
                    background: 'Hover.lowEmphasis',
                  }}
                  aria-label={t('component:layouts.main_layout.language')}
                  as={Button}
                  backgroundColor="transparent"
                  color="Text.lowEmphasis"
                  fontWeight="normal"
                  height="fit-content"
                  padding="2"
                  textAlign="left"
                  width="full"
                >
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    width="full"
                  >
                    <Flex alignItems="center" gap="2">
                      <FaGlobeRegular />
                      {locale === 'ja'
                        ? t('component:layouts.main_layout.language_japanese')
                        : t('component:layouts.main_layout.language_english')}
                    </Flex>
                    <FaChevronUpRegular />
                  </Flex>
                </MenuTrigger>
                <MenuPositioner>
                  <MenuContent>
                    <MenuRadioItemGroup
                      value={locale}
                      onValueChange={(details): void => {
                        if (isSupportedLanguage(details.value)) {
                          setLanguage(details.value)
                        }
                      }}
                    >
                      <MenuRadioItem value="ja">
                        {t('component:layouts.main_layout.language_japanese')}
                      </MenuRadioItem>
                      <MenuRadioItem value="en">
                        {t('component:layouts.main_layout.language_english')}
                      </MenuRadioItem>
                    </MenuRadioItemGroup>
                  </MenuContent>
                </MenuPositioner>
              </MenuRoot>
              <Separator marginY="1" />
              <SideMenuLayoutPopOverMenuItem
                label={t('component:layouts.main_layout.sign_out')}
                onClick={(): void => logout()}
              />
            </>
          }
          userIconSrc={user?.picture ?? ''}
          userName={user?.name ?? ''}
        />
      </Box>
    </>
  )
}
